/**
 * Planned-quantity copy/paste helpers for the Targets panel.
 * Operates on numeric matrices; Achieved is never copied/pasted.
 */

export type TargetsCellCoord = {
  rowIndex: number;
  colIndex: number;
};

export type TargetsSelection = {
  anchor: TargetsCellCoord;
  focus: TargetsCellCoord;
};

export type TargetsPasteResult = {
  attempted: number;
  applied: number;
  skipped: number;
};

export function normalizeSelection(selection: TargetsSelection): {
  rowStart: number;
  rowEnd: number;
  colStart: number;
  colEnd: number;
} {
  const rowStart = Math.min(selection.anchor.rowIndex, selection.focus.rowIndex);
  const rowEnd = Math.max(selection.anchor.rowIndex, selection.focus.rowIndex);
  const colStart = Math.min(selection.anchor.colIndex, selection.focus.colIndex);
  const colEnd = Math.max(selection.anchor.colIndex, selection.focus.colIndex);
  return { rowStart, rowEnd, colStart, colEnd };
}

export function selectionContains(
  selection: TargetsSelection | null,
  rowIndex: number,
  colIndex: number,
): boolean {
  if (!selection) return false;
  const { rowStart, rowEnd, colStart, colEnd } = normalizeSelection(selection);
  return (
    rowIndex >= rowStart &&
    rowIndex <= rowEnd &&
    colIndex >= colStart &&
    colIndex <= colEnd
  );
}

/** Serialize a matrix to TSV for system clipboard / Excel. */
export function serializeQtyMatrix(matrix: Array<Array<number | null>>): string {
  return matrix
    .map((row) =>
      row
        .map((v) => {
          if (v == null || !Number.isFinite(v) || v === 0) return '';
          return String(v);
        })
        .join('\t'),
    )
    .join('\n');
}

/**
 * Parse Excel/Sheets clipboard text (TSV / CSV-ish) into a number matrix.
 * Empty cells → null. Invalid tokens are skipped as null.
 */
export function parseClipboardQtyMatrix(text: string): Array<Array<number | null>> {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n+$/, '');
  if (!normalized.trim()) return [];

  const rows = normalized.split('\n');
  return rows.map((line) => {
    // Prefer tab (Excel); fall back to comma when no tabs present.
    const parts = line.includes('\t') ? line.split('\t') : line.split(',');
    return parts.map((cell) => {
      const trimmed = cell.trim();
      if (trimmed === '') return null;
      const n = Number(trimmed.replace(/,/g, ''));
      if (!Number.isFinite(n) || n < 0) return null;
      return n;
    });
  });
}

export function matrixDimensions(matrix: Array<Array<number | null>>): {
  rows: number;
  cols: number;
} {
  const rows = matrix.length;
  const cols = rows === 0 ? 0 : Math.max(...matrix.map((r) => r.length), 0);
  return { rows, cols };
}

/**
 * Excel-like paste footprint:
 * - 1×1 clipboard into a larger selection → fill the whole selection
 * - otherwise paste the clipboard matrix from the selection top-left
 */
export function resolvePasteMatrix(
  matrix: Array<Array<number | null>>,
  selectionRows: number,
  selectionCols: number,
): Array<Array<number | null>> {
  if (matrix.length === 0) return matrix;
  const { rows, cols } = matrixDimensions(matrix);
  const single = rows === 1 && cols <= 1;
  if (single && (selectionRows > 1 || selectionCols > 1)) {
    const value = matrix[0]?.[0] ?? null;
    return Array.from({ length: selectionRows }, () =>
      Array.from({ length: selectionCols }, () => value),
    );
  }
  return matrix;
}

export function isMultiCellMatrix(matrix: Array<Array<number | null>>): boolean {
  const { rows, cols } = matrixDimensions(matrix);
  return rows > 1 || cols > 1;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}
